import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { registerOptionsSchema } from '@app/shared';
import { apiGet, apiPost, ApiPath } from '../../lib/api';
import { InstitutionLogo, useInstitutionBrand } from '../../components/institution-brand';

type SekolahOption = { id: number; nama: string };
type JabatanOption = { id: number; nama: string };

export function RegisterPage() {
  const { nama_pesantren } = useInstitutionBrand();
  const [sekolahList, setSekolahList] = useState<SekolahOption[]>([]);
  const [jabatanList, setJabatanList] = useState<JabatanOption[]>([]);

  const [nama, setNama] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [konfirmasiPassword, setKonfirmasiPassword] = useState('');
  const [nomorTelepon, setNomorTelepon] = useState('');
  const [jenisKelamin, setJenisKelamin] = useState<'L' | 'P'>('L');
  const [sekolahId, setSekolahId] = useState<number | ''>('');
  const [jabatanId, setJabatanId] = useState<number | ''>('');
  const [roleCode, setRoleCode] = useState<'guru' | 'kepala_sekolah'>('guru');

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingOptions, setIsFetchingOptions] = useState(true);

  useEffect(() => {
    async function loadOptions() {
      try {
        const res = await apiGet(ApiPath.AUTH.REGISTER_OPTIONS, registerOptionsSchema);
        setSekolahList(res.sekolah);
        setJabatanList(res.jabatan);
      } catch (e) {
        console.error('Gagal memuat opsi registrasi:', e);
        setError('Gagal memuat daftar sekolah/jabatan');
      } finally {
        setIsFetchingOptions(false);
      }
    }
    loadOptions();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== konfirmasiPassword) {
      setError('Konfirmasi password tidak sama');
      return;
    }

    if (!sekolahId) {
      setError('Sekolah wajib dipilih');
      return;
    }

    if (!jabatanId) {
      setError('Jabatan wajib dipilih');
      return;
    }

    setIsLoading(true);

    try {
      await apiPost(
        ApiPath.AUTH.REGISTER,
        {
          nama: nama.trim(),
          email: email.trim().toLowerCase(),
          password,
          konfirmasi_password: konfirmasiPassword,
          nomor_telepon: nomorTelepon.trim(),
          jenis_kelamin: jenisKelamin,
          sekolah_id: Number(sekolahId),
          jabatan_id: Number(jabatanId),
          role_code: roleCode,
        },
        z.null(),
      );
      setSuccessMessage('Pendaftaran berhasil. Silakan tunggu persetujuan Admin.');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Registrasi gagal. Silakan coba lagi.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <main
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem 1rem',
      }}
    >
      <section
        style={{
          width: '100%',
          maxWidth: '480px',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          padding: '2rem',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          background: '#ffffff',
        }}
      >
        <div style={{ display: 'grid', placeItems: 'center', marginBottom: '0.75rem' }}>
          <InstitutionLogo size={64} />
        </div>
        <h1 style={{ textAlign: 'center', marginBottom: '0.25rem', fontSize: '1.25rem' }}>
          {nama_pesantren}
        </h1>
        <p
          style={{
            textAlign: 'center',
            color: '#6b7280',
            marginBottom: '1.5rem',
            fontSize: '0.875rem',
          }}
        >
          Registrasi Akun Baru
        </p>

        {successMessage ? (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div
              style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                borderRadius: '0.375rem',
                backgroundColor: '#dcfce7',
                color: '#15803d',
                fontWeight: 500,
              }}
            >
              {successMessage}
            </div>
            <Link
              to="/login"
              style={{
                display: 'inline-block',
                padding: '0.75rem 1.5rem',
                backgroundColor: '#2563eb',
                color: '#ffffff',
                borderRadius: '0.375rem',
                textDecoration: 'none',
                fontWeight: 600,
              }}
            >
              Kembali ke Login
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div
                role="alert"
                style={{
                  marginBottom: '1rem',
                  padding: '0.75rem',
                  borderRadius: '0.375rem',
                  backgroundColor: '#fee2e2',
                  color: '#991b1b',
                  fontSize: '0.875rem',
                }}
              >
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="nama"
                  style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                >
                  Nama Lengkap *
                </label>
                <input
                  id="nama"
                  type="text"
                  required
                  minLength={3}
                  maxLength={150}
                  value={nama}
                  onChange={(e) => setNama(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="email"
                  style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                >
                  Email *
                </label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <label
                    htmlFor="password"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Password *
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    htmlFor="konfirmasiPassword"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Konfirmasi Password *
                  </label>
                  <input
                    id="konfirmasiPassword"
                    type="password"
                    required
                    minLength={8}
                    value={konfirmasiPassword}
                    onChange={(e) => setKonfirmasiPassword(e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1rem',
                }}
              >
                <div>
                  <label
                    htmlFor="nomorTelepon"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Nomor HP *
                  </label>
                  <input
                    id="nomorTelepon"
                    type="tel"
                    required
                    minLength={8}
                    maxLength={20}
                    value={nomorTelepon}
                    onChange={(e) => setNomorTelepon(e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label
                    htmlFor="jenisKelamin"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Jenis Kelamin *
                  </label>
                  <select
                    id="jenisKelamin"
                    required
                    value={jenisKelamin}
                    onChange={(e) => setJenisKelamin(e.target.value as 'L' | 'P')}
                    style={inputStyle}
                  >
                    <option value="L">Laki-laki</option>
                    <option value="P">Perempuan</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label
                  htmlFor="sekolahId"
                  style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                >
                  Pilih Sekolah *
                </label>
                <select
                  id="sekolahId"
                  required
                  disabled={isFetchingOptions}
                  value={sekolahId}
                  onChange={(e) => setSekolahId(Number(e.target.value))}
                  style={inputStyle}
                >
                  <option value="">-- Pilih Sekolah --</option>
                  {sekolahList.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nama}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '0.75rem',
                  marginBottom: '1.5rem',
                }}
              >
                <div>
                  <label
                    htmlFor="roleCode"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Role Pendaftaran *
                  </label>
                  <select
                    id="roleCode"
                    required
                    value={roleCode}
                    onChange={(e) => setRoleCode(e.target.value as 'guru' | 'kepala_sekolah')}
                    style={inputStyle}
                  >
                    <option value="guru">Guru</option>
                    <option value="kepala_sekolah">Kepala Sekolah</option>
                  </select>
                </div>
                <div>
                  <label
                    htmlFor="jabatanId"
                    style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}
                  >
                    Pilih Jabatan *
                  </label>
                  <select
                    id="jabatanId"
                    required
                    disabled={isFetchingOptions}
                    value={jabatanId}
                    onChange={(e) => setJabatanId(Number(e.target.value))}
                    style={inputStyle}
                  >
                    <option value="">-- Pilih Jabatan --</option>
                    {jabatanList.map((j) => (
                      <option key={j.id} value={j.id}>
                        {j.nama}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || isFetchingOptions}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: 'none',
                  borderRadius: '0.375rem',
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  fontWeight: 600,
                  cursor: 'pointer',
                  opacity: isLoading || isFetchingOptions ? 0.7 : 1,
                  marginBottom: '1rem',
                }}
              >
                {isLoading ? 'Memproses...' : 'Daftar Akun'}
              </button>

              <div style={{ textAlign: 'center', fontSize: '0.875rem', color: '#6b7280' }}>
                Sudah punya akun?{' '}
                <Link
                  to="/login"
                  style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}
                >
                  Masuk di sini
                </Link>
              </div>
            </form>
          </>
        )}
      </section>
    </main>
  );
}

const inputStyle = {
  width: '100%',
  padding: '0.5rem',
  border: '1px solid #d1d5db',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  boxSizing: 'border-box' as const,
};
